import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectSession, selectSessionAdvisor, selectSessionIsLoading } from '../model/selectors';
import { advisorRequiredFields } from '@client/util/useAdvisorProfile';
import { SessionType } from '@shared/SessionType';
import { FullScreenLoadingOverlay } from '@client';

export function useAdvisorNavigation() {
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const location = useLocation();

  const navigateToActionRequiredView = () => {
    const actionRequiredPath = `/advisors/${advisor.advisorId}/account-action-required`;
    navigate(actionRequiredPath);
  };

  const navigateToClientsDashboard = () => {
    const clientsPath = `/advisors/${advisor.advisorId}/clients`;
    navigate(clientsPath);
  };

  const navigateToWelcomeDashboard = () => {
    const welcomePath = `/advisors/${advisor.advisorId}/welcome`;
    navigate(welcomePath);
  };

  const navigateToAdvisorProfile = (tab?: 'profile' | 'communication' | 'defaults') => {
    const advisorProfilePath = `/advisors/${advisor.advisorId}/settings`;
    if (tab) {
      navigate(`${advisorProfilePath}?tab=${tab}`);
    } else {
      navigate(advisorProfilePath);
    }
  };

  // const navigateToOpportunitiesDashboard = () => {
  //   const opportunitiesPath = `/advisors/${advisor.advisorId}/opportunities`;
  //   navigate(opportunitiesPath);
  // };

  const navigateToSponsorshipDashboard = () => {
    const sponsorshipPath = `/advisors/${advisor.advisorId}/sponsorship`;
    navigate(sponsorshipPath);
  };

  const isBasePath = (path: string) => {
    return (
      !!path.match(/^\/advisors\/[a-z0-9]{4}-[a-z0-9]{3}$/) ||
      path === `/advisors` ||
      path === `/advisors/`
    );
  };

  return {
    navigateToActionRequiredView,
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
    navigateToAdvisorProfile,
    // navigateToOpportunitiesDashboard,
    navigateToSponsorshipDashboard,
    isBasePath,
    currentPath: location.pathname,
  };
}

interface RouteGuardProps {
  children: React.ReactNode;
}

export function AdvisorDashboardRouteGuard({ children }: RouteGuardProps) {
  const isSessionLoading = useSelector(selectSessionIsLoading);
  const { sessionType, advisorHasClients } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const {
    navigateToActionRequiredView,
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
    isBasePath,
    currentPath,
    navigateToAdvisorProfile,
  } = useAdvisorNavigation();

  const isAdvisorSessionType =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  useEffect(() => {
    if (isSessionLoading) {
      return;
    }

    const missingRequiredFields = advisorRequiredFields.filter(field => !advisor[field as keyof AdvisorProfile]);

    if (
      // force navigate to the account action required page
      [SessionType.advisorUnverifiedAccount, SessionType.advisorInactiveSubscription].includes(
        sessionType,
      ) &&
      !currentPath.includes('account-action-required')
    ) {
      navigateToActionRequiredView();
    } else if (
      // force navigate to the advisor profile/settings
      isAdvisorSessionType &&
      !currentPath.includes('settings') &&
      missingRequiredFields.length > 0
    ) {
      navigateToAdvisorProfile();
    } else if (
      // force navigate to the clients dashboard
      isAdvisorSessionType &&
      advisorHasClients &&
      (isBasePath(currentPath) ||
        currentPath.includes('account-action-required') ||
        currentPath.includes('welcome'))
    ) {
      navigateToClientsDashboard();
    } else if (
      // force navigate to the welcome dashboard
      isAdvisorSessionType &&
      !advisorHasClients &&
      (isBasePath(currentPath) ||
        currentPath.includes('account-action-required') ||
        currentPath.includes('clients'))
    ) {
      navigateToWelcomeDashboard();
    }
  }, [
    isSessionLoading,
    sessionType,
    currentPath,
    advisor,
    advisorHasClients,
    isAdvisorSessionType,
    isBasePath,
    navigateToActionRequiredView,
    navigateToAdvisorProfile,
    navigateToClientsDashboard,
    navigateToWelcomeDashboard,
  ]);

  return (
    <>
      <FullScreenLoadingOverlay isLoading={isSessionLoading} />
      {children}
    </>
  );
}
